import React, {ReactElement} from "react";
import {BaseFragment, BaseFragmentProps, BaseFragmentState} from "../shared/BaseFragment";
import {
  StyledBoxColumn,
  StyledBoxRow,
  StyledContainer,
  StyledHeadingVariant,
  StyledSpan
} from "../shared/StyledComponents";
import {BORDER_RADIUS_LARGE, PAGE_FRAGMENT_SM_WIDTH, PD_XLG, PD_XLG2, SZ_JUMBO, SZ_LG} from "../shared/dimens";
import {Grid, Typography} from "@mui/material";
import {UserDisplayName} from "../shared/types";
import {getMemberAuth} from "../shared/auth";
import peppinLogo from "./app-shared/common/res/peppin_logotype.svg";
import {SHOWCASE_ITEM_TYPE_METADATAS, ShowcaseItemType} from "./types";
import {LargeButton} from "./Components";
import {lightGray} from "../shared/colors";
import {PathProps} from "../index";

export type HomeFragmentProps = BaseFragmentProps & {}

type HomeFragmentState = BaseFragmentState & {}

function Section(props: { title: string, text: string, style?: any, children?: any }): ReactElement {
  return <StyledContainer size={"xxlg"} style={{alignItems: "center", padding: "8%", ...props.style}}>
    <StyledHeadingVariant default_={3} max={4} style={{textAlign: "center"}}>{props.title}</StyledHeadingVariant>
    <Typography style={{textAlign: "center"}}>{props.text}</Typography>
    {props.children}
  </StyledContainer>
}

function GetStartedButton(props: { path: PathProps }): ReactElement {
  const memberAuth = getMemberAuth();
  return memberAuth?.auth?.currentUser
    ? <LargeButton
      onClick={() => props.path.navigate("/@"+memberAuth.member.username)}>
      {"Continue as " + UserDisplayName(memberAuth.member.user) + " →"}
    </LargeButton>
    : <LargeButton
      onClick={() => props.path.navigate("/login")}>
      Get Started →
    </LargeButton>;
}

export class HomeFragment extends BaseFragment<HomeFragmentProps, HomeFragmentState> {

  protected renderContainerContent(): React.ReactElement {
    return <div style={{display: "flex", flexDirection: "column", position: "relative"}}>
      <StyledBoxColumn style={{background: `linear-gradient(white, #eee)`}}>
        <StyledContainer size="xxlg"
                         style={{marginTop: SZ_LG, minHeight: "60vh", padding: "4%"}}>
          <Grid container style={{height: "100%"}}>
            <Grid xs={12} sm={6} md={7}>
              <StyledBoxColumn style={{height: "100%", justifyContent: "center"}}>
                <img src={"/icon.png"}
                     style={{width: 96, borderRadius: BORDER_RADIUS_LARGE, border: "2px solid white"}}/>
                <StyledHeadingVariant default_={2} style={{lineHeight: 1.1, letterSpacing: -2.1}}>
                  Showcase your creativity.</StyledHeadingVariant>
                <StyledHeadingVariant default_={5} style={{fontWeight: "normal"}}>
                  A place for artists, designers & creative people to showcase what they're making, get inspiration, and
                  to connect with each another.
                </StyledHeadingVariant>
                <StyledBoxRow>
                  <GetStartedButton path={this.props.path}/>
                  <StyledSpan/>
                </StyledBoxRow>
              </StyledBoxColumn>
            </Grid>
            <Grid xs={12} sm={6} md={5}>
              <StyledBoxColumn style={{height: "100%", justifyContent: "end"}}>
                <img src={"/images/hero.svg"} style={{transform: "scaleX(-1)", padding: PD_XLG, width: "100%"}}/>
              </StyledBoxColumn>
            </Grid>
          </Grid>
        </StyledContainer>
      </StyledBoxColumn>
      <Section
        title={"Sharing made simple."}
        text={"Upload any type of media, or embed with links to your work from other platform, to share instantly."}>
        <>
          <Grid container>
            {[ShowcaseItemType.IMAGE, ShowcaseItemType.VIDEO, ShowcaseItemType.AUDIO, ShowcaseItemType.SCRATCH_PROJECT].map(itemType => {
              const metadata = SHOWCASE_ITEM_TYPE_METADATAS.findMetadata(itemType);
              return <Grid xs={6} md={3}>
                <div style={{display: "flex", width: "100%", height: SZ_JUMBO, padding: PD_XLG}}>
                  <StyledBoxColumn
                    style={{width: "100%", background: lightGray, alignItems: "center", justifyContent: "center"}}>
                    <metadata.iconType style={{color: metadata.color, width: 72, height: 72}}/>
                    <Typography variant={"body2"}>{metadata.name}</Typography>
                  </StyledBoxColumn>
                </div>
              </Grid>;
            })}
          </Grid>
          <Typography>... and many more.</Typography>
        </>
      </Section>
      <StyledBoxColumn style={{background: lightGray}}>
        <Section
          style={{padding: "16%"}}
          title={"Unlock your potential"}
          text={"People are sharing original, innovative & fun ideas all over the world on Showcase. See what's trending right now."}>
          <StyledBoxRow>
            <LargeButton
              variant="outlined"
              onClick={() => this.props.path.navigate("/trending")}>
              See what's trending
            </LargeButton>
          </StyledBoxRow>
        </Section>
      </StyledBoxColumn>
      <StyledContainer
        size="xlg"
        style={{
          maxWidth: PAGE_FRAGMENT_SM_WIDTH,
          alignItems: "center",
          width: "100%",
          margin: "auto",
          padding: PD_XLG2
        }}>
        <Typography
          variant="h4"
          style={{
            textAlign: "center",
            padding: PD_XLG,
            marginBottom: -PD_XLG
          }}>
          The world's your oyster
        </Typography>
        <GetStartedButton path={this.props.path}/>
      </StyledContainer>
      <StyledBoxColumn style={{alignItems: "center", padding: PD_XLG2}}>
        <a href={"https://fyne.studio"} target="_blank">
          <img src={peppinLogo} style={{
            width: 96,
            opacity: 0.5,
          }}/></a>
        <Typography variant="body2" style={{textAlign: "center"}}>
          {`Copyright © ${new Date().getFullYear()}`}
          &nbsp;
          <a href={"https://superfyne.com"}>Superfyne LLC.</a>
          &nbsp;All rights reserved.
        </Typography>
      </StyledBoxColumn>
    </div>;
  }
}
